import { Flex, Icon, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/table-core";
import { useCallback } from "react";
import {
  RiCurrencyLine,
  RiDeleteBinLine,
  RiPencilLine,
  RiWallet3Line,
} from "react-icons/ri";
import { NoDataTable } from "src/components/Feedback/NoDataTable";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { PaginatedTable } from "src/components/Table/PaginatedTable";
import { SortType } from "src/components/Table/SortButton";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { maybePluralize } from "src/services/format";
import { Payment } from "./types";

interface PaymentsListProps {
  payments: Payment[];
  limit: number;
  offset: number;
  count: number;
  onFetchMore: (limit: number, offset: number) => void;
  onChangeSort: (columnName: string, sortType: SortType) => void;
  onClearSearch: () => void;
  onEdit: (paymentId: string) => void;
  onDelete: (paymentId: string) => void;
  onRequestPayment: (paymentId: string) => void;
  isLoading?: boolean;
  isPaymentConfigured: boolean;
}

export const PaymentsList: React.FC<PaymentsListProps> = ({
  payments,
  limit,
  offset,
  count,
  onFetchMore,
  onChangeSort,
  onClearSearch,
  onEdit,
  onDelete,
  onRequestPayment,
  isLoading = false,
  isPaymentConfigured,
}) => {
  const handleChangeSort = useCallback(
    (headerId: string, sortType: SortType) => {
      onChangeSort(headerId, sortType);
    },
    [onChangeSort]
  );

  const columns: ColumnDef<Payment>[] = [
    {
      id: "name",
      header: "Payment name",
      cell: (props) => (
        <TextWithOverflowTooltip
          content={props.row.original.name}
          maxWidth="15em"
        />
      ),
    },
    {
      id: "description",
      header: "Payment description",
      cell: (props) => (
        <TextWithOverflowTooltip
          content={props.row.original.description}
          maxWidth="20em"
        />
      ),
    },
    {
      id: "amount",
      header: "Amount",
      cell: (props) => <Text>${props.row.original.amount.toFixed(2)}</Text>,
    },
    {
      id: "inUse",
      header: "In use",
      cell: (props) => (
        <Text>
          {props.row.original.inUseCount > 0
            ? `${props.row.original.inUseCount} ${maybePluralize(
                props.row.original.inUseCount,
                "use"
              )}`
            : "Not in use"}
        </Text>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (props) => (
        <Flex direction="row" gap="2">
          <WithUserPermissions permissions={["payments:delete"]}>
            <Tooltip label="Delete" placement="left" hasArrow>
              <IconButton
                aria-label="Delete payment"
                icon={<Icon as={RiDeleteBinLine} />}
                variant="outline"
                colorScheme="gray"
                onClick={() => onDelete(props.row.original.id)}
                isDisabled={
                  props.row.original.inUseCount > 0 || !isPaymentConfigured
                }
              />
            </Tooltip>
          </WithUserPermissions>
          <WithUserPermissions permissions={["payments:update"]}>
            <Tooltip label="Edit" placement="left" hasArrow>
              <IconButton
                aria-label="Edit payment"
                icon={<Icon as={RiPencilLine} />}
                variant="outline"
                colorScheme="gray"
                onClick={() => onEdit(props.row.original.id)}
                isDisabled={!isPaymentConfigured}
              />
            </Tooltip>
          </WithUserPermissions>
          <WithUserPermissions permissions={["payments:create"]}>
            <Tooltip
              label={
                isPaymentConfigured
                  ? "Request payment"
                  : "Complete payment setup before requesting a payment."
              }
              placement="left"
              hasArrow
            >
              <IconButton
                aria-label="Request payment"
                icon={<Icon as={RiWallet3Line} />}
                variant="outline"
                colorScheme="gray"
                onClick={() => onRequestPayment(props.row.original.id)}
                isDisabled={!isPaymentConfigured}
              />
            </Tooltip>
          </WithUserPermissions>
        </Flex>
      ),
    },
  ];

  if (isLoading) {
    return (
      <PaginatedTable
        columns={columns}
        data={[]}
        count={0}
        limit={limit}
        offset={offset}
        onFetchMore={onFetchMore}
        onChangeSort={handleChangeSort}
        sortableColumnIds={["name", "description", "amount", "inUse"]}
        isLoading={true}
      />
    );
  }

  if (payments.length === 0) {
    return (
      <NoDataTable
        icon={RiCurrencyLine}
        text="No payments found"
        buttonText="Clear search"
        onButtonAction={onClearSearch}
      />
    );
  }

  return (
    <PaginatedTable
      columns={columns}
      data={payments}
      count={count}
      limit={limit}
      offset={offset}
      onFetchMore={onFetchMore}
      onChangeSort={handleChangeSort}
      sortableColumnIds={["name", "description", "amount", "inUse"]}
      isLoading={isLoading}
    />
  );
};
